import React from 'react';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MicIcon from '@mui/icons-material/Mic';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useHistory } from 'react-router-dom';
import { appRoutes } from 'router/router';
import wpGeodirServices from 'services/wpGeodirServices';
import toast from 'react-hot-toast';

const formatDeadline = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const s = String(dateString).trim();
  if (!s) return '';

  // Handle "#-# weeks" or "#-# week" - already has weeks, normalize to "X-Y weeks"
  const alreadyWeeksMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*weeks?$/i);
  if (alreadyWeeksMatch) return `${alreadyWeeksMatch[1]}-${alreadyWeeksMatch[2]} weeks`;

  // Handle week range format "#-#" (e.g. "1-2", "3-4") - ensure "weeks" is shown
  const weekRangeMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*$/);
  if (weekRangeMatch) return `${weekRangeMatch[1]}-${weekRangeMatch[2]} weeks`;

  // Handle "Week #-#" or "Week #-# weeks" - keep as is or ensure weeks suffix
  const weekPrefixMatch = s.match(/^week\s+(\d+)\s*-\s*(\d+)\s*(weeks?)?$/i);
  if (weekPrefixMatch) {
    const suffix = weekPrefixMatch[3] ? '' : ' weeks';
    return `Week ${weekPrefixMatch[1]}-${weekPrefixMatch[2]}${suffix}`;
  }

  try {
    const date = new Date(s);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const displayValue = (v: any) => {
  const s = v == null ? '' : String(v).trim();
  return s.length > 0 ? s : '—';
};

interface ProgramDrawerProps {
  open: boolean;
  onClose: () => void;
  item: any;
  isTrial?: boolean;
  isSaved?: boolean;
  isSaving?: boolean;
  onSaveToggle?: (id: number, apiCalled?: boolean) => void;
  disableSave?: boolean; // Starter at 6 saved limit - disable save for new programs
}

const ELIGIBILITY_PREVIEW_LENGTH = 80;

const ProgramDrawer: React.FC<ProgramDrawerProps> = ({
  open,
  onClose,
  item,
  isTrial = true,
  isSaved = false,
  isSaving = false,
  onSaveToggle,
  disableSave = false,
}) => {
  const history = useHistory();
  const [localIsSaved, setLocalIsSaved] = React.useState(isSaved);
  const [localIsUnsaving, setLocalIsUnsaving] = React.useState(false);

  React.useEffect(() => {
    setLocalIsSaved(isSaved);
  }, [isSaved]);

  if (!item) return null;

  const title = item?.programName ?? item?.program_name ?? item?.post_title ?? '';
  const institution = item?.institution || item?.post_author_name || '';
  const location =
    [item?.city, item?.stateProvince ?? item?.state_province, item?.country].filter(Boolean).join(', ') ||
    item?.location ||
    (item?.offerIn ?? item?.offer_in) ||
    '';
  const costMin = item?.costMin ?? item?.cost_min;
  const costMax = item?.costMax ?? item?.cost_max;
  const costRange =
    costMin != null || costMax != null
      ? [costMin, costMax]
          .filter((v: any) => v != null && `${v}`.trim() !== '')
          .map((v: any) => (typeof v === 'number' ? `$${v}` : v))
          .join(' - ')
      : '';
  const cost = item?.cost != null ? (typeof item.cost === 'number' ? `$${item.cost}` : item.cost) : (costRange || item?.price || '');
  const primaryCategory = (item?.primaryCategory ?? item?.primary_category) || item?.categoryName || '';
  const subCategory = (item?.subCategory ?? item?.sub_category) || '';
  const delivery = (item?.programDelivery ?? item?.program_delivery) || item?.delivery || item?.format || '';
  const residential = (item?.residentialOption ?? item?.residential_option) || '';
  const duration = item?.duration || '';
  const appOpen = formatDeadline(item?.applicationOpenIn ?? item?.app_open_date ?? item?.application_open_date);
  const appDeadline = formatDeadline(
    item?.applicationDeadline ?? item?.app_deadline ?? item?.application_deadline ?? item?.application_due ?? item?.deadline
  );
  const programDatesRaw = item?.programDates ?? item?.program_dates;
  const programDates = programDatesRaw
    ? (typeof programDatesRaw === 'string' ? programDatesRaw : formatDeadline(programDatesRaw))
    : '';
  const eligibility = item?.eligibility || item?.grades || '';
  const eligibilityPreview =
    eligibility && eligibility.length > ELIGIBILITY_PREVIEW_LENGTH
      ? `${eligibility.slice(0, ELIGIBILITY_PREVIEW_LENGTH).trim()}…`
      : eligibility;

  const handleOpenFullDetails = () => {
    onClose();
    history.push({
      pathname: appRoutes.summerProgramsDetail.path,
      search: `id=${item.id}`,
    });
  };

  const handleSaveToggle = async () => {
    if (isSaving || localIsUnsaving || disableSave || !onSaveToggle) return;
    if (localIsSaved) {
      setLocalIsUnsaving(true);
      try {
        await wpGeodirServices.unsaveProgram(item.id);
        setLocalIsSaved(false);
        toast.success('Program removed from saved list');
        onSaveToggle(item.id, true);
      } catch (error: any) {
        console.error('Error unsaving program:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to remove program';
        toast.error(errorMessage);
        setLocalIsSaved(true);
      } finally {
        setLocalIsUnsaving(false);
      }
    } else {
      onSaveToggle(item.id, false);
    }
  };

  const handleManageApplications = () => {
    onClose();
    history.push(appRoutes.summerProgramsManager.path);
  };

  // Requirements preview: map from item or use placeholders
  const requirements: { label: string; icon: React.ReactNode; status: string }[] = [
    { label: 'Essays', icon: <DescriptionIcon sx={{ fontSize: '20px', color: '#6b7280' }} />, status: item?.requirements_essays ?? 'Required' },
    { label: 'Transcript', icon: <SchoolIcon sx={{ fontSize: '20px', color: '#6b7280' }} />, status: item?.requirements_transcript ?? 'Required' },
    { label: 'Recommendations', icon: <PersonAddIcon sx={{ fontSize: '20px', color: '#6b7280' }} />, status: item?.requirements_recommendations ?? 'Optional' },
    { label: 'Interview', icon: <MicIcon sx={{ fontSize: '20px', color: '#6b7280' }} />, status: item?.requirements_interview ?? 'Unknown' },
  ];

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'required') return '#dc2626';
    if (s === 'optional') return '#ea580c';
    return '#6b7280';
  };

  const drawerWidth = Math.min(480, window.innerWidth * 0.9);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } } }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          maxWidth: '100%',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 24px 16px',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#111827',
                  lineHeight: 1.3,
                }}
              >
                {displayValue(title)}
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: '15px', color: '#6b7280' }}>
                {displayValue(institution)}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={handleOpenFullDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '4px 0',
                  whiteSpace: 'nowrap',
                }}
              >
                Open Full Details
              </button>
              <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: '#6b7280' }}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {/* Program Snapshot */}
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Program Snapshot
            </h3>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {[
                { label: 'Primary Category', value: primaryCategory },
                { label: 'Sub-Category', value: subCategory },
                { label: 'Program Delivery', value: delivery },
                { label: 'Residential Option', value: residential },
                { label: 'Location', value: location },
                { label: 'Duration', value: duration },
                { label: 'Cost', value: cost },
              ].map((row, idx) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: idx < 6 ? '1px solid #e5e7eb' : 'none',
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>{row.label}</span>
                  <span style={{ color: '#111827', fontWeight: 500, textAlign: 'right' }}>
                    {displayValue(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Key Dates */}
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Key Dates
            </h3>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {[
                { label: 'Application Open', value: appOpen },
                { label: 'Application Deadline', value: appDeadline },
                { label: 'Program Dates', value: programDates },
              ].map((row, idx) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: idx < 2 ? '1px solid #e5e7eb' : 'none',
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>{row.label}</span>
                  <span
                    style={{
                      color: row.label === 'Application Deadline' ? '#dc2626' : '#111827',
                      fontWeight: row.label === 'Application Deadline' ? 600 : 500,
                      textAlign: 'right',
                    }}
                  >
                    {displayValue(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Eligibility */}
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Eligibility
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>
              {eligibilityPreview || '—'}
            </p>
            {eligibility && eligibility.length > ELIGIBILITY_PREVIEW_LENGTH && (
              <button
                type="button"
                onClick={handleOpenFullDetails}
                style={{
                  marginTop: '6px',
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Read more
              </button>
            )}
          </section>

          {/* Requirements Preview */}
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Requirements Preview
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {requirements.map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {r.icon}
                    <span style={{ fontSize: '14px', color: '#111827', fontWeight: 500 }}>{r.label}</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: getStatusColor(r.status) }}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Footer actions */}
        <div
          style={{
            padding: '20px 24px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            flexShrink: 0,
            backgroundColor: '#fff',
          }}
        >
          {/* Save + Open full details links - on top */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={handleSaveToggle}
              disabled={disableSave}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '13px',
                cursor: (isSaving || localIsUnsaving || disableSave) ? 'not-allowed' : 'pointer',
                opacity: disableSave ? 0.6 : 1,
              }}
              aria-label={localIsSaved ? 'Unsave' : 'Save'}
              title={disableSave ? 'Upgrade to save more programs' : undefined}
            >
              {localIsSaved ? (
                <StarIcon sx={{ fontSize: '20px', color: '#eab308' }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: '20px' }} />
              )}
              <span>{disableSave ? 'Save (limit reached)' : localIsSaved ? 'Saved' : 'Save'}</span>
            </button>
            <button
              type="button"
              onClick={handleOpenFullDetails}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '13px',
                cursor: 'pointer',
              }}
              aria-label="Open in new"
            >
              <OpenInNewIcon sx={{ fontSize: '20px' }} />
              <span>Open full details</span>
            </button>
          </div>
          {/* Save Program + Manage Applications buttons - vertical stack */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              type="button"
              disabled={isSaving || localIsUnsaving || disableSave}
              onClick={handleSaveToggle}
              title={disableSave ? 'Upgrade to save more programs' : undefined}
              style={{
                width: '100%',
                height: '44px',
                fontSize: '15px',
                fontWeight: 600,
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: (isSaving || localIsUnsaving || disableSave) ? 'not-allowed' : 'pointer',
                opacity: (isSaving || localIsUnsaving || disableSave) ? 0.7 : 1,
              }}
            >
              {disableSave ? 'Save (limit reached)' : localIsUnsaving ? 'Removing...' : isSaving ? 'Saving...' : localIsSaved ? 'Saved' : 'Save Program'}
            </button>
            {!isTrial && (
              <button
                type="button"
                onClick={handleManageApplications}
                style={{
                  width: '100%',
                  height: '44px',
                  fontSize: '15px',
                  fontWeight: 500,
                  backgroundColor: '#fff',
                  color: '#111827',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Manage Applications
              </button>
            )}
            {/* Trial: locked Manage Applications (Silver+) - below buttons */}
            {isTrial && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '9999px',
                  border: 'none',
                }}
              >
                <LockIcon sx={{ fontSize: '18px', color: '#4b5563' }} />
                <span>Manage Applications (Silver+)</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ProgramDrawer;
